import cloneDeep from 'lodash/cloneDeep';

import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import { Model, ModelData } from 'src/containers/ProjectDiagram/types/model';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { updateLinkIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateLinkIris';
import { updateModelAttributeIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateModelAttributeIris';
import { updateModelColorIdIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateModelColorIdIris';
import { updateModelDescriptionIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateModelDescriptionIris';
import { updateModelGeoDataIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateModelGeoDataIris';
import { updateModelHighlightedKeys } from 'src/containers/ProjectDiagram/utils/updateIris/updateModelHighlightedKeys';
import { updateModelLabelIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateModelLabelIris';
import { updateModelPropertyTypeIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateModelPropertyTypeIris';
import { updateModelSelectionKeys } from 'src/containers/ProjectDiagram/utils/updateIris/updateModelSelectionKeys';
import { updateNodeIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateNodeIris';
import { updateProjectModelIds } from 'src/containers/ProjectDiagram/utils/updateIris/updateProjectModelIds';
import { updateRuleIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateRuleIris';
import { updateSensitivePropertyGroupIris } from 'src/containers/ProjectDiagram/utils/updateIris/updateSensitivePropertyGroupIris';

export const updateIri =
  (prevIri: string, newIri: string) => (model: Model, diagram: Diagram) => {
    // 1. update node keys
    const nodeKeys = updateNodeIris(prevIri, newIri)(model);

    // 2. update link and link label keys, also relationshipIri for relationship mappings
    const linkKeys = updateLinkIris(prevIri, newIri)(model);

    // 3. update model attributes
    updateModelAttributeIris(prevIri, newIri)(model);

    // 4. update model labels
    updateModelLabelIris(prevIri, newIri)(model);

    // 5. update model descriptions
    updateModelDescriptionIris(prevIri, newIri)(model);

    // 6. update model colors
    updateModelColorIdIris(prevIri, newIri)(model);

    // 7. update model geo data
    updateModelGeoDataIris(prevIri, newIri)(model);

    // 8. update model property types
    updateModelPropertyTypeIris(prevIri, newIri)(model);

    // 9. update sensitive property groups
    updateSensitivePropertyGroupIris(prevIri, newIri)(model);

    // 10. update rules
    updateRuleIris(prevIri, newIri)(model);

    // 11. update hidden part
    const hiddenPartKeys = diagram.hiddenPart.updateAttributeValue(
      prevIri,
      newIri
    );

    const updatedKeys = {
      ...nodeKeys,
      ...linkKeys,
      ...hiddenPartKeys,
    };

    // 12. update model highlighted and selection
    updateModelHighlightedKeys(updatedKeys)(model);
    updateModelSelectionKeys(updatedKeys)(model);

    // 13. update modelsByIri
    updateProjectModelIds(prevIri, newIri)(model);

    // 14. update draft originalIrisByCurrentIri
    if (model.modelData.draftData) {
      const draftData = cloneDeep(model.modelData.draftData);
      const originalIri = draftData.originalIrisByCurrentIri[prevIri];
      if (originalIri) {
        if (originalIri !== newIri) {
          draftData.originalIrisByCurrentIri[newIri] = originalIri;
        }
        delete draftData.originalIrisByCurrentIri[prevIri];
      } else {
        draftData.originalIrisByCurrentIri[newIri] = prevIri;
      }
      setDataProperty<ModelData>(model)(
        model.modelData,
        'draftData',
        draftData
      );
    }
  };
